import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, Label, Select, Check, Honeypot } from "./FormBits";
import { PLATFORMS, SERVICES, CATEGORIES, AUDIENCES, errMsg } from "./marketingConstants";

const blank = (audience, user) => ({
  contact_name: user?.name || "", creator_name: "", email: user?.email || "", phone: "", location: "",
  affiliate_id: "", primary_platform: "", social_links: {}, follower_counts: {},
  content_niche: "", services: audience === "social_promotion" ? ["social_account_promotion"] : [],
  portfolio_url: "", collaboration_interest: "", preferred_contact: "email",
  goals: "", message: "", budget_range: "", consent: false,
  list_publicly: false, show_audience_figures: false, public_bio: "", avatar_url: "", featured_content_url: "",
  hp_field: "",
});

function SocialInputs({ form, setNested }) {
  return (
    <div className="space-y-2" data-testid="social-inputs">
      <Label>Social profiles * <span className="normal-case tracking-normal text-white/40">(public handles or URLs only — never share passwords)</span></Label>
      {PLATFORMS.map(([k, l, ph]) => (
        <div key={k} className="grid grid-cols-[90px_1fr_110px] gap-2 items-center">
          <span className="text-[11px] font-bebas tracking-wider uppercase text-white/60">{l}</span>
          <input className="input-lifted w-full text-sm" placeholder={ph} value={form.social_links[k] || ""}
            onChange={(e) => setNested("social_links", k, e.target.value)} data-testid={`social-${k}`} />
          <input className="input-lifted w-full text-sm" inputMode="numeric" placeholder="Followers"
            aria-label={`${l} follower count (self-reported)`} value={form.follower_counts[k] || ""}
            onChange={(e) => setNested("follower_counts", k, e.target.value.replace(/[^0-9]/g, ""))}
            data-testid={`followers-${k}`} />
        </div>
      ))}
      <p className="text-[10px] text-white/40">Follower counts are optional and self-reported. We don't verify or publish them unless you choose to below.</p>
    </div>
  );
}

function ServicePicker({ selected, toggle }) {
  return (
    <div>
      <Label>Services you'd like to request</Label>
      <div className="grid sm:grid-cols-2 gap-1.5 mt-1">
        {SERVICES.map(([k, l]) => (
          <button type="button" key={k} onClick={() => toggle(k)} data-testid={`service-opt-${k}`}
            className={`text-left text-xs px-3 py-2 rounded border transition-colors ${
              selected.includes(k) ? "border-lifted-green bg-lifted-green/10 text-lifted-green" : "border-white/15 text-white/70 hover:border-white/40"}`}>
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}

function PublicListing({ form, set }) {
  return (
    <div className="border border-white/10 rounded-lg p-4 space-y-3 bg-black/30">
      <Check checked={form.list_publicly} onChange={(v) => set("list_publicly", v)} testid="field-list-publicly">
        I'd like an optional public profile in the Lifted Creator Showcase. It stays private until a Lifted admin approves it, and I can edit or remove it anytime.
      </Check>
      {form.list_publicly && (
        <div className="space-y-3" data-testid="public-profile-fields">
          <div>
            <Label>Public bio</Label>
            <textarea className="input-lifted w-full min-h-[70px] text-sm" maxLength={400} value={form.public_bio}
              onChange={(e) => set("public_bio", e.target.value)} data-testid="field-public-bio" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Avatar image URL" value={form.avatar_url} onChange={(v) => set("avatar_url", v)} testid="field-avatar-url" placeholder="https://…" />
            <Field label="Featured content URL" value={form.featured_content_url} onChange={(v) => set("featured_content_url", v)} testid="field-featured-url" placeholder="https://…" />
          </div>
          <Check checked={form.show_audience_figures} onChange={(v) => set("show_audience_figures", v)} testid="field-show-figures">
            Show my self-reported follower counts on my public profile (labeled as self-reported).
          </Check>
        </div>
      )}
    </div>
  );
}

export function CreatorInquiryForm({ audience }) {
  const { user } = useAuth();
  const [form, setForm] = useState(() => blank(audience, user));
  const [status, setStatus] = useState({ kind: "idle", msg: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (k, sub, v) => setForm((f) => ({ ...f, [k]: { ...f[k], [sub]: v } }));
  const toggle = (s) => setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));
  const meta = AUDIENCES.find((a) => a.key === audience);

  async function submit(e) {
    e.preventDefault();
    if (!form.contact_name || !form.email || !form.goals) return setStatus({ kind: "err", msg: "Please fill in name, email, and goals." });
    if (!Object.values(form.social_links).some((v) => v.trim())) return setStatus({ kind: "err", msg: "Add at least one social profile handle or URL." });
    if (!form.consent) return setStatus({ kind: "err", msg: "Please confirm consent to submit." });
    setStatus({ kind: "loading", msg: "" });
    try {
      const body = { ...form, audience_type: audience, primary_platform: form.primary_platform || null,
        content_niche: form.content_niche || null, affiliate_id: form.affiliate_id || null };
      await api.post("/marketing/inquiries", body);
      setStatus({ kind: "ok", msg: form.list_publicly
        ? "Request received. Your showcase profile is pending admin review — nothing is public yet."
        : "Request received. The Lifted team will review it and reach out." });
      setForm(blank(audience, user));
    } catch (err) {
      setStatus({ kind: "err", msg: errMsg(err, "We couldn't submit your request. Please check your links and try again.") });
    }
  }

  return (
    <form onSubmit={submit} className="glass-panel p-6 space-y-4" data-testid={`creator-inquiry-form-${audience}`}>
      <Honeypot value={form.hp_field} onChange={(v) => set("hp_field", v)} />
      <div className="eyebrow text-lifted-green">{meta?.title}</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Your name *" value={form.contact_name} onChange={(v) => set("contact_name", v)} testid="cf-contact-name" />
        <Field label="Creator / display name" value={form.creator_name} onChange={(v) => set("creator_name", v)} testid="cf-creator-name" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Email *" type="email" value={form.email} onChange={(v) => set("email", v)} testid="cf-email" />
        <Field label="Phone" value={form.phone} onChange={(v) => set("phone", v)} testid="cf-phone" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Location" value={form.location} onChange={(v) => set("location", v)} testid="cf-location" placeholder="City, State" />
        <Field label="Lifted affiliate ID (optional)" value={form.affiliate_id} onChange={(v) => set("affiliate_id", v)} testid="cf-affiliate-id" placeholder="e.g. AFF-XXXXXXX" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Select label="Primary platform" value={form.primary_platform} onChange={(v) => set("primary_platform", v)} testid="cf-primary-platform" options={PLATFORMS.map(([k, l]) => [k, l])} />
        <Select label="Content niche" value={form.content_niche} onChange={(v) => set("content_niche", v)} testid="cf-niche" options={CATEGORIES} />
      </div>
      <SocialInputs form={form} setNested={setNested} />
      <ServicePicker selected={form.services} toggle={toggle} />
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Portfolio / media kit URL" value={form.portfolio_url} onChange={(v) => set("portfolio_url", v)} testid="cf-portfolio" placeholder="https://…" />
        <Select label="Preferred contact" value={form.preferred_contact} onChange={(v) => set("preferred_contact", v)} testid="cf-preferred-contact" placeholder="Email"
          options={[["email", "Email"], ["phone", "Phone"], ["instagram_dm", "Instagram DM"], ["tiktok_dm", "TikTok DM"], ["other", "Other"]]} />
      </div>
      <Field label="Budget range (optional)" value={form.budget_range} onChange={(v) => set("budget_range", v)} testid="cf-budget" placeholder="e.g. $0–$500" />
      <Field label="Collaboration interest" value={form.collaboration_interest} onChange={(v) => set("collaboration_interest", v)} testid="cf-collab" placeholder="Brands, products, or campaigns you'd like to work with" />
      <div>
        <Label>Goals *</Label>
        <textarea className="input-lifted w-full min-h-[90px]" value={form.goals} onChange={(e) => set("goals", e.target.value)} data-testid="cf-goals" placeholder="What do you want to achieve?" />
      </div>
      <div>
        <Label>Message</Label>
        <textarea className="input-lifted w-full min-h-[70px]" value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="cf-message" />
      </div>
      <PublicListing form={form} set={set} />
      <Check checked={form.consent} onChange={(v) => set("consent", v)} testid="cf-consent">
        I agree Lifted may contact me about this request. I understand services are requests subject to approval and availability, and Lifted does not guarantee followers, reach, or engagement. Submitting does not grant affiliate status or commissions.
      </Check>
      <button type="submit" disabled={status.kind === "loading"} className="btn-lifted w-full disabled:opacity-50" data-testid="creator-submit">
        {status.kind === "loading" ? "Sending…" : "Submit request"}
      </button>
      {status.kind === "ok" && <div className="flex items-center gap-2 text-sm text-lifted-green" data-testid="creator-success"><ShieldCheck className="h-4 w-4" /> {status.msg}</div>}
      {status.kind === "err" && <div className="text-sm text-rose-300" data-testid="creator-error">{status.msg}</div>}
      <p className="text-[11px] text-white/40 flex items-center gap-1">
        <Lock className="h-3 w-3" /> Your email, phone and notes stay private.{" "}
        {audience === "affiliate" && <>Not an affiliate yet? <Link to="/affiliate-zone" className="text-lifted-green hover:underline" data-testid="cf-affiliate-enroll-link">Join or log in here</Link>.</>}
      </p>
    </form>
  );
}
